/**
 * Board 3b · A scenario's live tickets: what Kim drafted, what the agent sent and the automatic grade, filtered by
 * good and not good, with Replay on the current protocol.
 */

import { useState } from 'react';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GradeBar } from '../grade-bar';
import type { LiveTicket, LiveType } from '../types';
import { scenarioLabelOf } from './scenarios';
import { useReplay } from '../use-replay';
import { useTypeTickets } from '../use-live-pool';
import { filterTickets, isGood, type TicketFilter } from './view-rules';
import { formatWhen } from './scenarios';

const Bubble = ({ label, text, tint }: { label: string; text: string | null; tint?: boolean }) => (
	<div>
		<div className='mb-0.5 text-xs text-gray-800'>{label}</div>
		<div className={cn('rounded-xl px-3 py-2 text-[13px] whitespace-pre-wrap', tint ? 'bg-blue-bg' : 'bg-gray-100')}>
			{text ?? <span className='text-gray-800'>Not recorded</span>}
		</div>
	</div>
);

const TicketCard = ({
	ticket,
	onReplay,
	replaying
}: {
	ticket: LiveTicket;
	onReplay: () => void;
	replaying: boolean;
}) => (
	<div className='flex flex-col gap-2 rounded-[14px] border border-gray-300 p-3.5'>
		<span className='flex flex-wrap items-center gap-2 text-[13px] font-semibold text-text-heading'>
			{ticket.customer?.name ?? `Ticket ${ticket.ticketId}`}
			{ticket.grade && <Badge variant={isGood(ticket) ? 'success' : 'destructive'}>{ticket.grade}</Badge>}
			<span className='text-xs font-normal text-gray-800'>{formatWhen(ticket.ranAt)}</span>
		</span>
		<Bubble label='Customer' text={ticket.description} />
		<Bubble label='Kim drafted' text={ticket.draft} tint />
		<Bubble label='Agent sent' text={ticket.sent} />
		{ticket.gradeReason && (
			<span className='text-xs text-gray-800'>
				{ticket.grade}: {ticket.gradeReason}
			</span>
		)}
		<span className='flex items-center gap-2'>
			<Button variant='outline' size='sm' onClick={onReplay} disabled={replaying}>
				{replaying && <Loader2 className='animate-spin' aria-hidden />}
				Replay
			</Button>
			{ticket.replay && (
				<span className='text-xs text-gray-800'>
					Last replay: {ticket.replay.status === 'done' ? (ticket.replay.bucket ?? 'not compared') : ticket.replay.status}
				</span>
			)}
		</span>
	</div>
);

export const ReviewView = ({ sopId, type, onBack }: { sopId: string; type: LiveType; onBack: () => void }) => {
	const { data: tickets, isPending, error } = useTypeTickets(sopId, type.typeId);
	const { replay, starting } = useReplay(sopId, type.typeId);
	const [filter, setFilter] = useState<TicketFilter>('all');
	const all = tickets ?? [];
	const good = all.filter(isGood).length;
	const shown = filterTickets(all, filter);
	const filters: Array<[TicketFilter, string]> = [
		['all', `All ${all.length}`],
		['bad', `Not good ${all.length - good}`],
		['good', `Good ${good}`]
	];
	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<div className='flex shrink-0 items-center gap-2 border-b border-gray-500 px-4 py-3'>
				<button type='button' onClick={onBack} aria-label='Back to the scenario' className='cursor-pointer rounded-md p-1 hover:bg-gray-100'>
					<ChevronLeft className='size-4' aria-hidden />
				</button>
				<div className='flex flex-col'>
					<span className='text-xs tracking-wide text-gray-800 uppercase'>Live tickets</span>
					<span className='text-[15px] font-semibold text-text-heading'>{scenarioLabelOf(type)}</span>
				</div>
			</div>
			<div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4'>
				<div className='flex justify-between text-[13px] text-gray-900'>
					<b className='font-semibold text-text-heading'>
						{type.ready.good} of {type.ready.graded} Perfect or Good Enough
					</b>
					<span>{type.ready.graded - type.ready.good} Incomplete or Incorrect</span>
				</div>
				<GradeBar grades={type.grades} />
				<p className='text-[13px] text-gray-800'>
					The reply is written from the Perfect and Good Enough tickets, and Ready counts the same grades.
				</p>
				<div role='group' aria-label='Filter tickets' className='inline-flex w-fit rounded-full bg-gray-100 p-0.5'>
					{filters.map(([key, label]) => (
						<button
							key={key}
							type='button'
							aria-pressed={filter === key}
							onClick={() => setFilter(key)}
							className={cn(
								'cursor-pointer rounded-full px-3 py-1 text-[13px] font-medium',
								filter === key ? 'bg-white text-text-heading shadow-sm' : 'text-gray-800'
							)}
						>
							{label}
						</button>
					))}
				</div>
				{isPending && (
					<p className='flex items-center gap-2 text-xs text-gray-800'>
						<Loader2 className='size-3.5 animate-spin' aria-hidden /> Reading the tickets…
					</p>
				)}
				{error && <p className='text-xs text-gray-800'>Could not read the tickets.</p>}
				{shown.map((ticket) => (
					<TicketCard
						key={ticket.scenarioId}
						ticket={ticket}
						replaying={starting}
						onReplay={() => void replay([ticket.scenarioId])}
					/>
				))}
			</div>
		</div>
	);
};
